import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import {
  explainEntryOutcome,
  explainOutcome,
  getOutcomeMeta,
} from "./injectOutcome";

function ResultRow({ result, explain }) {
  const meta = getOutcomeMeta(result.outcome);
  return (
    <li className="flex items-start justify-between gap-3 px-5 py-3">
      <div className="min-w-0">
        <p className="text-[13px] font-medium text-(--color-text-primary) truncate">
          {result.team_name || "Team"}
        </p>
        <p className="text-[11px] text-(--color-text-muted) leading-relaxed">
          {explain(result)}
        </p>
      </div>
      <span
        className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-semibold ${meta.badge}`}
      >
        {meta.label}
      </span>
    </li>
  );
}

export default function InjectResultsModal({ data, onClose }) {
  useEffect(() => {
    const handleKeyDown = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const results = data?.results || [];
  // Entry injects (noise/decoy/department) carry `family`; stage injects don't.
  const isEntry = Boolean(data.family);
  const explain = isEntry
    ? explainEntryOutcome
    : (result) => explainOutcome(result, data.stage);
  const title = isEntry
    ? `"${data.name}" → all teams`
    : `Stage ${data.stage} → all teams`;
  const summary = (
    isEntry
      ? [
          data.fired && `${data.fired} fired`,
          data.skipped_not_ready && `${data.skipped_not_ready} not ready`,
          data.box_error && `${data.box_error} errored`,
        ]
      : [
          data.advanced && `${data.advanced} injected`,
          data.skipped_ahead && `${data.skipped_ahead} ahead`,
          data.blocked_prerequisite && `${data.blocked_prerequisite} blocked`,
          data.skipped_not_ready && `${data.skipped_not_ready} not ready`,
          data.box_error && `${data.box_error} errored`,
        ]
  )
    .filter(Boolean)
    .join(" · ");

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-full max-w-md h-105 max-h-[85vh] rounded-xl border border-(--color-border) bg-(--color-bg) shadow-2xl overflow-hidden"
      >
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-(--color-border) shrink-0">
          <div className="min-w-0">
            <p
              className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isEntry ? "text-sky-400" : "text-orange-400"}`}
            >
              {isEntry ? `${data.family} results` : "Inject results"}
            </p>
            <h2 className="mt-0.5 text-base font-semibold text-(--color-text-primary)">
              {title}
            </h2>
            <p className="mt-0.5 text-xs text-(--color-text-muted)">
              {summary || `${data.total} team${data.total === 1 ? "" : "s"}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 -m-1 rounded-md text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-white/5 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <ul className="flex-1 min-h-0 overflow-y-auto py-1 divide-y divide-(--color-border)">
          {results.map((result) => (
            <ResultRow key={result.run_id} result={result} explain={explain} />
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
